import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import { setTheme } from '../../store/slices/themeSlice';
import { ThemeId } from '../../types/theme';
import { availableThemes } from '../../config/themes';
import {
  SwatchIcon,
  ChevronDownIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

interface ThemeDropdownProps {
  className?: string;
}

const ThemeDropdown: React.FC<ThemeDropdownProps> = ({ className = '' }) => {
  const dispatch = useDispatch<AppDispatch>();
  const currentTheme = useSelector((state: RootState) => state.theme.currentTheme);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentThemeData = availableThemes.find(t => t.id === currentTheme);

  const handleThemeChange = (themeId: ThemeId) => {
    dispatch(setTheme(themeId));
    setIsOpen(false);
  };

  const getThemeColors = (themeId: string) => {
    switch (themeId) {
      case 'kfc':
        return { primary: '#dc2626', secondary: '#111827' };
      case 'mcdonalds':
        return { primary: '#eab308', secondary: '#da1e37' };
      case 'subway':
        return { primary: '#00923f', secondary: '#ffdc00' };
      default:
        return { primary: '#f97316', secondary: '#64748b' };
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-1 md:space-x-2 px-2 md:px-3 py-2 text-gray-700 hover:text-primary-600 transition-colors rounded-lg hover:bg-gray-100"
        title={`Change Theme (Current: ${currentThemeData?.name || 'Theme'})`}
      >
        <SwatchIcon className="w-5 h-5" />
        <span className="hidden lg:inline text-sm font-medium">
          {currentThemeData?.name || 'Theme'}
        </span>
        <ChevronDownIcon className={`w-4 h-4 hidden md:block transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="p-3">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Choose Theme</h3>
            <div className="space-y-2">
              {availableThemes.map((theme) => {
                const colors = getThemeColors(theme.id);
                const isSelected = currentTheme === theme.id;

                return (
                  <button
                    key={theme.id}
                    onClick={() => handleThemeChange(theme.id as ThemeId)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all hover:bg-gray-50 ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      {/* Theme Color Preview */}
                      <div className="flex space-x-1">
                        <div
                          className="w-3 h-3 rounded-full border border-gray-300"
                          style={{ backgroundColor: colors.primary }}
                        />
                        <div
                          className="w-3 h-3 rounded-full border border-gray-300"
                          style={{ backgroundColor: colors.secondary }}
                        />
                      </div>

                      {/* Theme Name */}
                      <span className="text-sm font-medium text-gray-900">
                        {theme.name}
                      </span>
                    </div>

                    {/* Selected Indicator */}
                    {isSelected && (
                      <CheckIcon className="w-4 h-4 text-blue-500" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Theme Description */}
            <div className="mt-3 p-2 bg-gray-50 rounded text-xs text-gray-600">
              {currentTheme === 'kfc' && "Bold red and black styling inspired by KFC"}
              {currentTheme === 'mcdonalds' && "Golden yellow and red McDonald's branding"}
              {currentTheme === 'subway' && "Fresh green and yellow Subway theme"}
              {currentTheme === 'default' && "Classic warm orange restaurant theme"}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThemeDropdown;